import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SVG from "react-inlinesvg";
import moment from "moment";
import { useFormikContext } from "formik";
import { useIntl } from "react-intl";
import { getLang, isRLTLang } from "./../../../../../i18n";
import useHideAndShowField from "./hooks/useHideAndShowField";
import useResizeHook from "./hooks/useResizeHook";
import { FieldError } from "./FieldError";
import "moment/locale/ar-tn";
import "moment/locale/fr";
import "./DatePickerFieldWithInput.css"
import { FR, AR } from "../../../../../constants";
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import  {fr, arTN}  from 'date-fns/locale';
import { getAttr, toAbsoluteUrl } from "src/helpers";
import _ from "lodash";
import useResetDateRangePickerField from './hooks/useResetDateRangePickerField';
import useResetDatePickerWithInput from './hooks/useResetDatePickerWithInput';

const locale = {
  [FR]: "fr",
  [AR]: "ar-tn",
};

// Register the locales
registerLocale('fr', fr);
registerLocale('ar-tn', arTN);

// Consistent date format constant
const DATE_FORMAT = "DD/MM/YYYY";
const DISPLAY_FORMAT = "dd/MM/yyyy"; // Format for react-datepicker

const DatePickerFieldWithInputMapi = ({
  field, // { name, value, onChange, onBlur }
  form, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  label,
  relatedTo,
  changeDateValue,
  icon = true,
  isValidDate = () => true,
  errorValidation = () => "Invalid date format. Please use DD/MM/YYYY.",
  inputGroupClassName = "form-group",
  inputClassName = "form-control-date-picker form-control",
  type = "date",
  format = DISPLAY_FORMAT,
  placeholder,
  size = 12,
  dateFormat = true,
  timeFormat = false,
  required = false,
  disabled = false,
  maxLength = 256,
  initialValue = null,
  rdv_sais_occas = false,
  initialValueChainedField = "",
  resetConditionValue = undefined,
  mapi = false,
  emit,
  eventName,
  ...props
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [manualInputValue, setManualInputValue] = useState(initialValue || "");
  const [isValid, setIsValid] = useState(true);
  const [customError, setCustomError] = useState(null);
  const intl = useIntl();
  const formik = useFormikContext();
  const [isDisabled, setIsDisabled] = useState(false);
  const { value } = field;
  const [width, height] = useResizeHook();
  
  useResetDatePickerWithInput({ 
    formik, 
    fieldName: field.name, 
    setSelectedDate, 
    format: DATE_FORMAT
  });

  const iconStyle = isRLTLang()
    ? {
        position: "absolute",
        height: "1.125rem",
        minWidth: "1.25rem",
        top: "calc(100% - 1.875rem)",
        right: "calc(100% - 2.5rem)",
        cursor: "pointer",
      }
    : {
        position: "absolute",
        height: "1.125rem",
        minWidth: "1.25rem",
        top: "calc(100% - 1.875rem)",
        left: "calc(100% - 2.5rem)",
        cursor: "pointer",
      };

  const adjustDatePickerPosition = () => {
    let elements = [...document.getElementsByClassName("rdt")];
    elements.forEach((element) => {
      const elementRect = element.getBoundingClientRect();
      const spaceBelow = window.innerHeight - elementRect.bottom;
      let popElements = [...document.getElementsByClassName("rdtPicker")];
      popElements.forEach((popElement) => {
        if (spaceBelow < 250) {
          popElement.classList.add("flip-Position");
        } else {
          popElement.classList.remove("flip-Position");
        }
      });
    });
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Always update the input field value
    setManualInputValue(inputValue);
    setCustomError(null);
    
    // Validate the date
    const date = moment(inputValue, DATE_FORMAT, true);
    
    if (date.isValid() && date.format(DATE_FORMAT) === inputValue) {
      // If valid and matches the exact format
      setIsValid(true);
      setSelectedDate(date.toDate());
      
      // Set related fields if needed
      if (relatedTo && changeDateValue) {
        form.setFieldValue(relatedTo, changeDateValue(date));
      }
      
      form.setFieldValue(field.name, date.format(DATE_FORMAT));
    } else {
      // If invalid format, don't clear the input but mark as invalid
      setIsValid(false);
      form.setFieldValue(field.name, ""); // Clear the field value in form
    }
  };

  const handleDateChange = (date) => {
    if (!date) {
      // Handle empty date selection
      setSelectedDate(null);
      setManualInputValue("");
      setIsValid(true); // Empty is valid
      form.setFieldValue(field.name, "");
      return;
    }
    
    const momentDate = moment(date);
    
    // Check if the date is valid according to custom validation
    if (isValidDate(momentDate, formik.values)) {
      const formattedDate = momentDate.format(DATE_FORMAT);
      
      setSelectedDate(date);
      setManualInputValue(formattedDate);
      setIsValid(true);
      setCustomError(null);
      
      // Set related fields if needed
      if (relatedTo && changeDateValue) {
        form.setFieldValue(relatedTo, changeDateValue(momentDate));
      }
      
      form.setFieldValue(field.name, formattedDate);
      
      if (emit) {
        emit({ type: eventName, payload: formattedDate });
      }
    } else {
      // Date is invalid according to custom validation
      setSelectedDate(null);
      setManualInputValue("");
      setIsValid(false);
      setCustomError(errorValidation(momentDate, formik.values));
      form.setFieldValue(field.name, "");
    }
  };

  const handleInputBlur = () => {
    // If input is empty, treat as valid
    if (!manualInputValue) {
      setIsValid(true);
      setCustomError(null);
      setSelectedDate(null);
      form.setFieldValue(field.name, "");
      return;
    }
    
    // Validate the date
    const date = moment(manualInputValue, DATE_FORMAT, true);
    
    if (!date.isValid()) {
      // Invalid date format
      setIsValid(false);
      setCustomError(intl.formatMessage({ id: "Invalid date format. Please use DD/MM/YYYY." }));
      setSelectedDate(null);
      form.setFieldValue(field.name, "");
    } else if (!isValidDate(date, formik.values)) {
      // Date format is valid but doesn't pass custom validation
      setIsValid(false);
      setCustomError(errorValidation(date, formik.values));
      setSelectedDate(null);
      form.setFieldValue(field.name, "");
    } else {
      // Valid date
      setIsValid(true);
      setCustomError(null);
      setSelectedDate(date.toDate());
      form.setFieldValue(field.name, date.format(DATE_FORMAT));
    }
  };

  const radioFields = ['radio', 'parentType', 'inscriptionNumberType'];

  // Reset values when radio fields change
  useEffect(() => {
    const hasRadioChanged = radioFields.some(fieldName => 
      formik?.values?.[fieldName]
    );

    if (hasRadioChanged) {
      setCustomError(null);
      setManualInputValue("");
      setSelectedDate(null);
      form.setFieldValue(field.name, "");
    }
  }, [formik?.values?.radio, formik?.values?.parentType, formik?.values?.inscriptionNumberType, field.name, form]);

  // Adjust datepicker position when window size changes
  useEffect(() => {
    adjustDatePickerPosition();
  }, [height, width]);

  // Initialize the selected date from the field value
  useEffect(() => {
    if (value) {
      const date = moment(value, DATE_FORMAT, true);
      if (date.isValid()) {
        setSelectedDate(date.toDate());
      }
    }
  }, []);

  // Handle chained field resetting
  useEffect(() => {
    if (!_.isEmpty(initialValueChainedField) && resetConditionValue) {
      const initValChainedField = getAttr(formik.values, initialValueChainedField, undefined);
      
      if (!initValChainedField || initValChainedField === resetConditionValue) {
        // Reset values
        form.setFieldValue(field.name, "");
        formik.setFieldValue(field.name, "");
        setSelectedDate(null);
        setManualInputValue("");
        setIsDisabled(false);
      } else {
        // Set to initial value and disable
        form.setFieldValue(field.name, initialValue);
        formik.setFieldValue(field.name, initialValue);
        setIsDisabled(true);
      }
    }
    setCustomError(null);
  }, [getAttr(formik.values, initialValueChainedField)]);

  // Update field display when value or other properties change
  useEffect(() => {
    if (mapi) {
      if (value) {
        const date = moment(value, DATE_FORMAT);
        if (date.isValid()) {
          setSelectedDate(date.toDate());
          setManualInputValue(date.format(DATE_FORMAT));
        } else {
          setManualInputValue(value);
        }
      } else {
        // Set initial value when field is empty
        setManualInputValue(initialValue || "");
        setSelectedDate(null);
      }
    } else {
      // Skip processing if no value or specific conditions
      const initValChainedField = getAttr(formik.values, initialValueChainedField, undefined);
      if (!value || ((!rdv_sais_occas || !value) && (!initValChainedField || _.isEmpty(initValChainedField)))) {
        return;
      }

      let date;
      if (_.isString(value) && !_.isEmpty(value)) {
        date = moment(value, DATE_FORMAT);
      }
      if (_.isDate(value)) {
        date = moment(value);
      }
      
      if (date && date.isValid()) {
        setSelectedDate(date.toDate());
        const formattedDate = date.format(DATE_FORMAT);
        setManualInputValue(formattedDate);
        form.setFieldValue(field.name, formattedDate);
        
        if (relatedTo && changeDateValue) {
          form.setFieldValue(relatedTo, changeDateValue(date));
        }
      } else {
        setSelectedDate(null);
        setManualInputValue(value || "");
        
        if (initialValue) {
          const initialDate = moment(initialValue);
          const formattedDate = initialDate.isValid() ? 
            initialDate.format(DATE_FORMAT) : null;
          form.setFieldValue(field.name, formattedDate);
        } else {
          form.setFieldValue(field.name, null);
        }
      }
    }
  }, [mapi, value, format, initialValue]);

  const fieldCSSClasses = form.errors[field.name] && form.touched[field.name] ? "is-invalid" : "";
  const dir = isRLTLang() ? "rtl" : "ltr";
  const conditionalShowAndHideClassName = useHideAndShowField({
    formik,
    fieldName: field.name,
    ...props,
  });

  const filterDate = (date) => {
    return isValidDate(moment(date), formik.values);
  };

  // Determine the current locale
  const currentLocale = locale[getLang()] || 'fr';
  setDefaultLocale(currentLocale);

  return (
    <div 
      dir={dir} 
      className={`${inputGroupClassName} ${conditionalShowAndHideClassName} col-lg-${size} datepickerWithInput`}
    >
      {label && (
        <label className="custom-label-style">
          {label} <span>{required && "*"}</span>
        </label>
      )}
      <div className="position-relative" style={{ width: "100%" }}>
        {icon && (
          <SVG
            src={toAbsoluteUrl("/media/svg/dateIcon.svg")}
            style={iconStyle}
            onClick={() => document.getElementById('date-input').focus()}
          />
        )}
        <DatePicker
          id="date-input"
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat={dateFormat ? format : ""}
          placeholderText={isRLTLang() ? "سنة/شهر/يوم" : "JJ/MM/AAAA"}
          className={`date-picker-full-width ${inputClassName} ${fieldCSSClasses} ${(disabled || isDisabled) && 'disabled'}`}
          locale={currentLocale}
          disabled={disabled || isDisabled}
          onChangeRaw={handleInputChange}
          onBlur={handleInputBlur}
          filterDate={filterDate}
          showYearDropdown
          showMonthDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          dropdownMode="select"
          style={{
            width: "100%",
            padding: ".625rem",
            textAlign: "start",
            backgroundColor: "#FAFAFA",
            height: 40,
            borderColor: isValid ? '' : 'red',
          }}
          autoComplete="off"
          {...props}
        />
      </div>
      {!isValid && customError && <div className="invalid-feedback">{customError}</div>}
      {isValid && !customError && <FieldError fieldName={field.name} />}
    </div>
  );
};

export default DatePickerFieldWithInputMapi;